import { Head } from "@inertiajs/react"
import { Card, Code, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { z } from "zod"

import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas/project"
import { SegmentSchema } from "@/schemas/segment"

const PropsSchema = z.object({
  project: ProjectSchema,
  segments: z.array(SegmentSchema),
  segment_membership_count: z.record(z.string(), z.number()),
})

type Props = z.infer<typeof PropsSchema>

export default function SegmentsIndex(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { segments, project } = validatedProps
  const membershipCounts = validatedProps.segment_membership_count

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Segments" },
      ]}
    >
      <Head title="Segments" />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            Segments
          </Heading>

          {/* <LinkButton href={newProjectSegmentPath(project.slug)}>
            <Plus className="mr-1 h-4 w-4" />
            New segment
          </LinkButton> */}
        </Flex>

        <Text>
          Segments can be added by editing the <code>segments.yml</code> file in the project root.
        </Text>

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Entry type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Entry condition</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Exit condition</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Customers count</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {segments.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No segments found
                  </Table.Cell>
                </Table.Row>
              ) : (
                segments.map((segment) => (
                  <Table.Row key={segment.id}>
                    <Table.Cell>{segment.id}</Table.Cell>
                    <Table.Cell>{segment.name}</Table.Cell>
                    <Table.Cell>{segment.description}</Table.Cell>
                    <Table.Cell>{segment.condition.type}</Table.Cell>
                    <Table.Cell>
                      <Code>
                        {segment.condition.type === "event"
                          ? segment.condition.event
                          : `${segment.condition.negate ? "!" : ""}${segment.condition.field} ${segment.condition.negate ? "!=" : "="} ${segment.condition.value}`}
                      </Code>
                    </Table.Cell>
                    <Table.Cell>
                      <Code>
                        {segment.condition.exit_after_days
                          ? `${segment.condition.event} (${segment.condition.exit_after_days} days)`
                          : "None"}
                      </Code>
                    </Table.Cell>
                    <Table.Cell>{membershipCounts[segment.id]}</Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
